import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';
import type {
  AnalysisRun,
  AnalysisResultRow,
  CreateAnalysisRunPayload,
} from '@/types/analysis';

export function useAnalysisRuns(page: number, limit: number, status?: string) {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['analysis-runs', { page, limit, status }],
    queryFn: async (): Promise<AnalysisRun[]> => {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      if (status) params.append('status', status);

      const response = await apiFetch(
        `${API_URL}/admin/analysis/runs?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message ||
            'Failed to fetch analysis runs'
        );
      }

      return await response.json();
    },
  });
}

export function useAnalysisRun(runId: string | null) {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['analysis-run', runId],
    queryFn: async (): Promise<AnalysisRun> => {
      const token = await getAccessTokenSilently();
      const response = await apiFetch(
        `${API_URL}/admin/analysis/runs/${runId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message || 'Failed to fetch analysis run'
        );
      }

      return await response.json();
    },
    enabled: !!runId,
  });
}

export function useAnalysisResults(
  runId: string | null,
  page: number,
  limit: number
) {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['analysis-results', runId, { page, limit }],
    queryFn: async (): Promise<AnalysisResultRow[]> => {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      const response = await apiFetch(
        `${API_URL}/admin/analysis/runs/${runId}/results?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message ||
            'Failed to fetch analysis results'
        );
      }

      return await response.json();
    },
    enabled: !!runId,
  });
}

export function useCreateAnalysisRun() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: CreateAnalysisRunPayload
    ): Promise<AnalysisRun> => {
      const token = await getAccessTokenSilently();
      const response = await apiFetch(`${API_URL}/admin/analysis/runs`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          formatApiError(errorData.detail, 'Failed to create analysis run')
        );
      }

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analysis-runs'] });
    },
  });
}

function formatApiError(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((e) => {
        if (!e || typeof e !== 'object') return null;
        const err = e as { loc?: unknown[]; msg?: string };
        const loc = Array.isArray(err.loc)
          ? err.loc.filter((p) => p !== 'body').join('.')
          : '';
        const msg = err.msg ?? '';
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (parts.length) return parts.join('; ');
  }
  return fallback;
}

export function useExportAnalysisResults() {
  const { getAccessTokenSilently } = useAuth0();

  const exportResults = async (runId: string) => {
    const token = await getAccessTokenSilently();
    const response = await apiFetch(
      `${API_URL}/admin/analysis/runs/${runId}/export`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to export results');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analysis-${runId}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return { exportResults };
}

export function useAnalysisAccess() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  return useQuery({
    queryKey: ['analysis-access'],
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<boolean> => {
      const token = await getAccessTokenSilently();
      const response = await apiFetch(`${API_URL}/admin/analysis/access`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message ||
            'Failed to check analysis access'
        );
      }

      const data = (await response.json()) as { allowed: boolean };
      return data.allowed;
    },
  });
}

export function useAnalysisToolNames(enabled: boolean) {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['analysis-tool-names'],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      const token = await getAccessTokenSilently();
      const response = await apiFetch(`${API_URL}/admin/analysis/tool-names`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message ||
            'Failed to fetch analysis tool names'
        );
      }

      return await response.json();
    },
  });
}
